/**
 * Copyright IBM Corp. 2022, 2026
 */

import { type MutableRefObject, useState, useRef, useEffect } from 'react'

/**
 * Access an arbitrary element's hover state. Returns a ref and the current hovered status:
 *
 * ```ts
 * const [hoverRef, isHovered] = useHover()
 * ```
 */
export function useHover<T extends HTMLElement>(): [
	hoverRef: MutableRefObject<T | null>,
	isHovered: boolean,
] {
	const [value, setValue] = useState(false)

	const ref = useRef<T>(null)
	const handleMouseOver = () => setValue(true)
	const handleMouseOut = () => setValue(false)

	useEffect(() => {
		const node = ref.current
		if (node) {
			node.addEventListener('mouseover', handleMouseOver)
			node.addEventListener('mouseout', handleMouseOut)

			return () => {
				node.removeEventListener('mouseover', handleMouseOver)
				node.removeEventListener('mouseout', handleMouseOut)
			}
		}
	}, [])

	return [ref, value]
}
